import React, { useEffect } from "react";
import { Link } from "react-router-dom";

import "../../assets/css/Cartattva.css";
import wiseLogo from "../../assets/img/clients/WiseTech Logo.jpg";

/* =========================================================
   PAGE SEO
========================================================= */

const PAGE_URL =
  "https://hexxatech.co.in/case-studies/wisetech";

const PAGE_TITLE =
  "WiseTech ERP & Software License Management Platform | HexxaTech";

const PAGE_DESCRIPTION =
  "Explore the WiseTech ERP and software license management platform developed by HexxaTech. The solution connects quotations, purchase orders, invoices, billing, client registration and centralized software license validation.";

const PAGE_KEYWORDS =
  "WiseTech ERP, WiseTech software, ERP software development, software license management system, license management software, custom ERP software, quotation management software, purchase order management software, invoice management software, billing software, license validation software, ASP.NET MVC development, SQL Server development, HexxaTech Softwares";

/* =========================================================
   FEATURES
========================================================= */

const features = [
  {
    title: "Quotation Management",
    text: "Create and manage client quotations with structured product, pricing and commercial information.",
  },
  {
    title: "Purchase Order Management",
    text: "Upload client purchase orders or generate purchase orders directly through the ERP workflow.",
  },
  {
    title: "Proforma Invoice",
    text: "Generate proforma invoices based on orders and maintain complete transaction visibility before final invoicing.",
  },
  {
    title: "Invoice Management",
    text: "Generate professional invoices from approved orders and proforma invoices while maintaining connected transaction records.",
  },
  {
    title: "Bill Generation",
    text: "Generate bills based on invoices or proforma invoices, creating a connected financial document workflow.",
  },
  {
    title: "Software License Management",
    text: "Manage software licenses, activation information, license records and client assignments from a centralized platform.",
  },
  {
    title: "Client & Software Registration",
    text: "Register clients and associate their purchased software products and licenses with centralized records.",
  },
  {
    title: "Real-time License Validation",
    text: "Validate software licenses against centralized license information and ensure only valid licenses are accepted.",
  },
  {
    title: "License Status & Information",
    text: "View license status, client information, software details and other important license-related information from one place.",
  },
  {
    title: "Connected ERP Workflow",
    text: "Connect sales, purchasing, invoicing, billing and software licensing into a single centralized business system.",
  },
];

/* =========================================================
   BUSINESS IMPACT
========================================================= */

const impact = [
  "Centralized business and license information",
  "Connected quotation-to-invoice workflow",
  "Simplified purchase order management",
  "Faster proforma and invoice generation",
  "Structured billing and financial document flow",
  "Centralized software license management",
  "Real-time license validation",
  "Better visibility into client software registrations",
  "Reduced manual work across business operations",
  "Single platform for ERP and license management",
];

/* =========================================================
   TECHNOLOGY
========================================================= */

const tech = [
  "ASP.NET MVC",
  "SQL Server",
  "Entity Framework",
  "REST APIs",
];

/* =========================================================
   SCREENSHOT TITLES
========================================================= */

const screenshotTitles = {
  "Customers.png": "Customer Management",
  "Generate Bill.png": "Bill Generation",
  "Licenses details.png": "License Details",
  "Licenses.png": "License Management",
  "PO Details.png": "Purchase Order Details",
  "Purchase Orders.png": "Purchase Order Management",
  "Transaction Reciept.jpeg": "Transaction Receipt",
};

/* =========================================================
   SCREENSHOT ORDER
========================================================= */

const screenshotOrder = [
  "Customers.png",
  "Generate Bill.png",
  "Licenses details.png",
  "Licenses.png",
  "PO Details.png",
  "Purchase Orders.png",
  "Transaction Reciept.jpeg",
];

/* =========================================================
   WISETECH SCREENSHOTS

   IMPORTANT:
   This uses require.context because the project is using
   Create React App / Webpack.

   DO NOT replace this with import.meta.glob().
========================================================= */

const wisetechImages = require.context(
  "../../assets/img/WiseTech",
  false,
  /\.(png|jpe?g|jpeg|webp|gif)$/i
);

const screenshotData = wisetechImages
  .keys()
  .map((file) => {
    const fileName = file.replace("./", "");

    return {
      fileName,
      image: wisetechImages(file),
    };
  })
  .sort((a, b) => {
    const aIndex = screenshotOrder.indexOf(a.fileName);
    const bIndex = screenshotOrder.indexOf(b.fileName);

    if (aIndex === -1 && bIndex === -1) {
      return a.fileName.localeCompare(b.fileName);
    }

    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    return aIndex - bIndex;
  });

/* =========================================================
   ERP WORKFLOW
========================================================= */

const workflow = [
  {
    number: "01",
    title: "Quotation",
    text: "Create and manage client quotations with structured commercial information.",
  },
  {
    number: "02",
    title: "Purchase Order",
    text: "Upload a client PO or generate a purchase order through the ERP workflow.",
  },
  {
    number: "03",
    title: "Proforma / Invoice",
    text: "Generate a proforma invoice or directly generate an invoice from the transaction workflow.",
  },
  {
    number: "04",
    title: "Billing",
    text: "Generate the corresponding bill from the invoice or proforma invoice.",
  },
  {
    number: "05",
    title: "License Registration",
    text: "Register software, client and license information in a centralized system.",
  },
  {
    number: "06",
    title: "License Validation",
    text: "Validate licenses and retrieve current license information from the centralized platform.",
  },
];

/* =========================================================
   HELPER FUNCTIONS
========================================================= */

const getAbsoluteAssetUrl = (asset) => {
  if (!asset) return PAGE_URL;

  if (
    typeof asset === "string" &&
    (asset.startsWith("http://") || asset.startsWith("https://"))
  ) {
    return asset;
  }

  return `${window.location.origin}${asset}`;
};

/* =========================================================
   COMPONENT
========================================================= */

export default function WiseTech() {
  /* =========================================================
     SEO
  ========================================================= */

  useEffect(() => {
    document.title = PAGE_TITLE;

    const logoUrl = getAbsoluteAssetUrl(wiseLogo);

    /* ---------------------------------------------------------
       META HELPER
    --------------------------------------------------------- */

    const setMeta = (
      name,
      content,
      attribute = "name"
    ) => {
      if (!content) return;

      let element = document.head.querySelector(
        `meta[${attribute}="${name}"]`
      );

      if (!element) {
        element = document.createElement("meta");

        element.setAttribute(attribute, name);

        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    /* ---------------------------------------------------------
       LINK HELPER
    --------------------------------------------------------- */

    const setLink = (rel, href) => {
      let element = document.head.querySelector(
        `link[rel="${rel}"]`
      );

      if (!element) {
        element = document.createElement("link");

        element.setAttribute("rel", rel);

        document.head.appendChild(element);
      }

      element.setAttribute("href", href);
    };

    /* =========================================================
       BASIC SEO
    ========================================================= */

    setMeta(
      "description",
      PAGE_DESCRIPTION
    );

    setMeta(
      "keywords",
      PAGE_KEYWORDS
    );

    setMeta(
      "robots",
      "index, follow"
    );

    setMeta(
      "author",
      "HexxaTech Softwares"
    );

    setMeta(
      "viewport",
      "width=device-width, initial-scale=1"
    );

    /* =========================================================
       CANONICAL
    ========================================================= */

    setLink(
      "canonical",
      PAGE_URL
    );

    /* =========================================================
       OPEN GRAPH
    ========================================================= */

    setMeta(
      "og:type",
      "article",
      "property"
    );

    setMeta(
      "og:title",
      PAGE_TITLE,
      "property"
    );

    setMeta(
      "og:description",
      PAGE_DESCRIPTION,
      "property"
    );

    setMeta(
      "og:url",
      PAGE_URL,
      "property"
    );

    setMeta(
      "og:site_name",
      "HexxaTech Softwares",
      "property"
    );

    setMeta(
      "og:locale",
      "en_IN",
      "property"
    );

    setMeta(
      "og:image",
      logoUrl,
      "property"
    );

    setMeta(
      "og:image:alt",
      "WiseTech ERP and Software License Management Platform",
      "property"
    );

    setMeta(
      "og:image:type",
      "image/jpeg",
      "property"
    );

    /* =========================================================
       TWITTER
    ========================================================= */

    setMeta(
      "twitter:card",
      "summary_large_image"
    );

    setMeta(
      "twitter:title",
      PAGE_TITLE
    );

    setMeta(
      "twitter:description",
      PAGE_DESCRIPTION
    );

    setMeta(
      "twitter:image",
      logoUrl
    );

    setMeta(
      "twitter:image:alt",
      "WiseTech ERP and Software License Management Platform"
    );

    /* =========================================================
       STRUCTURED DATA
    ========================================================= */

    const existingSchema = document.getElementById(
      "wisetech-case-study-schema"
    );

    if (existingSchema) {
      existingSchema.remove();
    }

    const schema = {
      "@context": "https://schema.org",

      "@graph": [
        /* -----------------------------------------------------
           WEB PAGE
        ----------------------------------------------------- */

        {
          "@type": "WebPage",

          "@id": `${PAGE_URL}#webpage`,

          url: PAGE_URL,

          name: PAGE_TITLE,

          description: PAGE_DESCRIPTION,

          isPartOf: {
            "@type": "WebSite",

            name: "HexxaTech Softwares",

            url: "https://hexxatech.co.in/",
          },

          breadcrumb: {
            "@id": `${PAGE_URL}#breadcrumb`,
          },

          mainEntity: {
            "@id": `${PAGE_URL}#software`,
          },
        },

        /* -----------------------------------------------------
           ARTICLE / CASE STUDY
        ----------------------------------------------------- */

        {
          "@type": "Article",

          "@id": `${PAGE_URL}#case-study`,

          headline:
            "WiseTech ERP & Software License Management Platform",

          description: PAGE_DESCRIPTION,

          url: PAGE_URL,

          image: {
            "@type": "ImageObject",

            url: logoUrl,

            caption:
              "WiseTech ERP and Software License Management Platform",
          },

          author: {
            "@type": "Organization",

            name: "HexxaTech Softwares",

            url: "https://hexxatech.co.in/",
          },

          publisher: {
            "@type": "Organization",

            name: "HexxaTech Softwares",

            url: "https://hexxatech.co.in/",
          },

          mainEntityOfPage: {
            "@id": `${PAGE_URL}#webpage`,
          },

          about: {
            "@id": `${PAGE_URL}#software`,
          },
        },

        /* -----------------------------------------------------
           SOFTWARE APPLICATION
        ----------------------------------------------------- */

        {
          "@type": "SoftwareApplication",

          "@id": `${PAGE_URL}#software`,

          name: "WiseTech",

          alternateName:
            "WiseTech ERP & Software License Management Platform",

          applicationCategory:
            "BusinessApplication",

          applicationSubCategory:
            "ERP Software",

          operatingSystem: "Web",

          description:
            "A centralized ERP and software license management platform for quotations, purchase orders, proforma invoices, invoices, billing, client registration and real-time license validation.",

          creator: {
            "@type": "Organization",

            name: "HexxaTech Softwares",

            url: "https://hexxatech.co.in/",
          },
        },

        /* -----------------------------------------------------
           BREADCRUMB
        ----------------------------------------------------- */

        {
          "@type": "BreadcrumbList",

          "@id": `${PAGE_URL}#breadcrumb`,

          itemListElement: [
            {
              "@type": "ListItem",

              position: 1,

              name: "Home",

              item: "https://hexxatech.co.in/",
            },

            {
              "@type": "ListItem",

              position: 2,

              name: "Case Studies",

              item:
                "https://hexxatech.co.in/case-studies",
            },

            {
              "@type": "ListItem",

              position: 3,

              name: "WiseTech ERP",

              item: PAGE_URL,
            },
          ],
        },
      ],
    };

    /* =========================================================
       ADD JSON-LD
    ========================================================= */

    const script = document.createElement("script");

    script.id =
      "wisetech-case-study-schema";

    script.type =
      "application/ld+json";

    script.textContent =
      JSON.stringify(schema);

    document.head.appendChild(script);

    /* =========================================================
       CLEANUP
    ========================================================= */

    return () => {
      const schemaElement =
        document.getElementById(
          "wisetech-case-study-schema"
        );

      if (schemaElement) {
        schemaElement.remove();
      }
    };
  }, []);

  /* =========================================================
     UI
  ========================================================= */

  return (
    <main
      className="cs-page"
      itemScope
      itemType="https://schema.org/WebPage"
    >
      <div className="cs-container">

        {/* =====================================================
            BREADCRUMB
        ===================================================== */}

        <a
          href="/case-studies"
          className="cs-breadcrumb"
          aria-label="Back to HexxaTech case studies"
        >
          ← Back to Case Studies
        </a>

        {/* =====================================================
            HERO
        ===================================================== */}

        <section
          className="cs-hero"
          aria-labelledby="wisetech-page-title"
        >

          <div className="cs-eyebrow">

            <span
              className="cs-eyebrow-dot"
              aria-hidden="true"
            />

            ERP & License Management Case Study

          </div>

          <div className="cs-logo">

            <img
              src={wiseLogo}
              alt="WiseTech ERP software logo"
              width="180"
              height="60"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />

          </div>

          <h1 id="wisetech-page-title">
            WiseTech ERP & Software License Management Platform
          </h1>

          <p className="cs-subtitle">
            Business ERP & Software License Management Platform
          </p>

          <p className="cs-hero-description">
            A centralized business platform combining ERP operations
            and software license management — connecting quotations,
            purchase orders, proforma invoices, invoices, billing,
            client registrations and real-time license validation
            into one integrated system.
          </p>

          <div className="cs-trust">

            <div className="cs-trust-item">
              <span
                className="cs-trust-dot"
                aria-hidden="true"
              />

              Complete ERP
            </div>

            <div className="cs-trust-item">
              <span
                className="cs-trust-dot"
                aria-hidden="true"
              />

              License Management
            </div>

            <div className="cs-trust-item">
              <span
                className="cs-trust-dot"
                aria-hidden="true"
              />

              Real-time Validation
            </div>

            <div className="cs-trust-item">
              <span
                className="cs-trust-dot"
                aria-hidden="true"
              />

              Custom Software Development
            </div>

          </div>

        </section>

        {/* =====================================================
            OVERVIEW
        ===================================================== */}

        <section
          className="cs-section-header"
          aria-labelledby="overview-title"
        >

          <div className="cs-section-label">
            Overview
          </div>

          <h2 id="overview-title">
            From business transactions to software licensing.
          </h2>

        </section>

        <section
          className="cs-split"
          aria-label="WiseTech project challenge and solution"
        >

          {/* CHALLENGE */}

          <article className="cs-panel">

            <div
              className="cs-panel-label"
              style={{
                color: "#2563eb",
              }}
            >
              The Challenge
            </div>

            <p>
              WiseTech required a centralized system to manage its
              day-to-day business transactions as well as software
              license operations. The existing workflow involved
              managing quotations, purchase orders, proforma invoices,
              invoices, billing, client registrations and software
              licenses across different processes.
            </p>

            <p>
              A major challenge was the manual software license renewal
              and extension process. Whenever a client's license needed
              to be renewed, extended or updated, the team had to
              manually modify the license information or generate and
              upload a new license file. Only after this manual
              intervention would the updated license become active.
            </p>

            <p>
              As the number of clients and licenses increased, this
              process became time-consuming and prone to manual errors.
              The team also had to spend significant time tracking
              license validity, updating license details and ensuring
              that the correct license files were deployed to clients.
            </p>

            <p>
              WiseTech needed an automated and centralized solution that
              could manage the complete business workflow while allowing
              software licenses to be renewed, extended and validated
              centrally without requiring repeated manual license file
              updates.
            </p>

          </article>

          {/* SOLUTION */}

          <article className="cs-panel">

            <div
              className="cs-panel-label"
              style={{
                color: "#7c3aed",
              }}
            >
              Our Solution
            </div>

            <p>
              HexxaTech developed an integrated ERP and License
              Management Platform that connects the complete commercial
              workflow with centralized software licensing operations.
            </p>

            <p>
              The platform streamlines the business process from
              quotation and purchase order to proforma invoice, invoice
              and billing while maintaining centralized records of
              clients, software products and licenses.
            </p>

            <p>
              The license management module eliminates the need for
              repeated manual license file updates. Administrators can
              extend, renew, activate or update licenses centrally, with
              changes reflected automatically for the respective client.
            </p>

            <p>
              The system also provides real-time license validation,
              expiry tracking and license status information, giving the
              team better visibility and control over active and expired
              licenses.
            </p>

            <p>
              This automation reduces manual effort, minimizes errors
              and makes the overall license management process faster,
              more reliable and scalable.
            </p>

          </article>

        </section>

        {/* =====================================================
            ERP WORKFLOW
        ===================================================== */}

        <section
          className="cs-section-header"
          aria-labelledby="workflow-title"
        >

          <div className="cs-section-label">
            ERP Workflow
          </div>

          <h2 id="workflow-title">
            A connected business transaction workflow.
          </h2>

        </section>

        <section
          className="cs-workflow"
          aria-label="WiseTech ERP workflow"
        >

          {workflow.map((item) => (
            <article
              className="cs-workflow-card"
              key={item.number}
            >

              <div
                className="cs-workflow-number"
                aria-hidden="true"
              >
                {item.number}
              </div>

              <h3>
                {item.title}
              </h3>

              <p>
                {item.text}
              </p>

            </article>
          ))}

        </section>

        {/* =====================================================
            KEY FEATURES
        ===================================================== */}

        <section
          className="cs-section-header"
          aria-labelledby="features-title"
        >

          <div className="cs-section-label">
            Key Features
          </div>

          <h2 id="features-title">
            Built around the complete business workflow.
          </h2>

        </section>

        <section
          className="cs-grid"
          aria-label="WiseTech ERP features"
        >

          {features.map((feature, index) => (
            <article
              className="cs-feature-card"
              key={feature.title}
            >

              <div
                className="cs-feature-icon"
                aria-hidden="true"
              >
                {String(index + 1).padStart(2, "0")}
              </div>

              <h3>
                {feature.title}
              </h3>

              <p>
                {feature.text}
              </p>

            </article>
          ))}

        </section>

        {/* =====================================================
            TECHNOLOGY
        ===================================================== */}

        <section
          className="cs-section-header"
          aria-labelledby="technology-title"
        >

          <div className="cs-section-label">
            Technology
          </div>

          <h2 id="technology-title">
            Technology behind the WiseTech platform.
          </h2>

        </section>

        <section
          className="cs-tech-row"
          aria-label="WiseTech technology stack"
        >

          {tech.map((technology) => (
            <span
              className="cs-tech-badge"
              key={technology}
            >
              {technology}
            </span>
          ))}

        </section>

        {/* =====================================================
            BUSINESS IMPACT
        ===================================================== */}

        <section
          className="cs-section-header"
          aria-labelledby="impact-title"
        >

          <div className="cs-section-label">
            Business Impact
          </div>

          <h2 id="impact-title">
            What changed for the business.
          </h2>

        </section>

        <section
          className="cs-impact-grid"
          aria-label="WiseTech business impact"
        >

          {impact.map((item) => (
            <div
              className="cs-impact-item"
              key={item}
            >

              <span
                className="cs-impact-check"
                aria-hidden="true"
              >
                ✓
              </span>

              <span>
                {item}
              </span>

            </div>
          ))}

        </section>

        {/* =====================================================
            SCREENSHOTS
        ===================================================== */}

        <section
          className="cs-section-header"
          aria-labelledby="screenshots-title"
        >

          <div className="cs-section-label">
            Screenshots
          </div>

          <h2 id="screenshots-title">
            See the WiseTech platform in action.
          </h2>

          <p className="cs-section-description">
            Selected screens from the WiseTech ERP and software
            license management platform developed by HexxaTech
            Softwares.
          </p>

        </section>

        <section
          className="cs-screenshot-grid"
          aria-label="WiseTech ERP platform screenshots"
        >

          {screenshotData.map((item) => {

            const title =
              screenshotTitles[item.fileName] ||
              item.fileName.replace(
                /\.[^/.]+$/,
                ""
              );

            return (
              <article
                className="cs-screenshot-card"
                key={item.fileName}
              >

                <div className="cs-screenshot-preview">

                  <img
                    src={item.image}
                    alt={`${title} screen from WiseTech ERP and software license management platform`}
                    loading="lazy"
                    decoding="async"
                  />

                </div>

                <div className="cs-screenshot-info">

                  <h3>
                    {title}
                  </h3>

                  <span>
                    WiseTech ERP & License Management
                  </span>

                </div>

              </article>
            );
          })}

        </section>

        {/* =====================================================
            RELATED SERVICES
        ===================================================== */}

        <section className="cs-section-header" aria-labelledby="wisetech-related-title">

          <div className="cs-section-label">
            Related Services
          </div>

          <h2 id="wisetech-related-title">
            Services that support this business platform.
          </h2>

        </section>

        <section className="cs-feature-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "42px" }} aria-label="WiseTech related services">
          <article className="cs-panel" style={{ padding: "22px" }}>
            <h3 style={{ marginTop: 0 }}>License Management</h3>
            <p>Centralize customer records, license data, validation and renewals in a governed platform.</p>
            <Link to="/services/license-management" style={{ color: "#2563eb", fontWeight: 700 }}>Explore License Management →</Link>
          </article>
          <article className="cs-panel" style={{ padding: "22px" }}>
            <h3 style={{ marginTop: 0 }}>Custom Software Development</h3>
            <p>Create a tailored business solution around your workflow, rules and operational requirements.</p>
            <Link to="/services/custom-software-development" style={{ color: "#2563eb", fontWeight: 700 }}>Explore Custom Software Development →</Link>
          </article>
          <article className="cs-panel" style={{ padding: "22px" }}>
            <h3 style={{ marginTop: 0 }}>ERP & Business Management</h3>
            <p>Connect commercial, operational and business management processes within one platform.</p>
            <Link to="/services/erp-business-management" style={{ color: "#2563eb", fontWeight: 700 }}>Explore ERP & Business Management →</Link>
          </article>
        </section>

        {/* =====================================================
            CTA
        ===================================================== */}

        <section
          className="cs-cta"
          aria-labelledby="wisetech-cta-title"
        >

          <div className="cs-cta-content">

            <h2 id="wisetech-cta-title">
              Have a business workflow
              <br />
              that needs to be connected?
            </h2>

            <p>
              Tell us about your business process, operational
              challenges and goals. We'll help you turn your workflow
              into a practical, scalable software solution built
              specifically for your business.
            </p>

            <a
              href="https://api.whatsapp.com/send/?phone=919226591664&text=Hello+HexxaTech%2C+I+would+like+to+discuss+a+software+project.&type=phone_number&app_absent=0"
              target="_blank"
              rel="noopener noreferrer"
              className="primary-button"
              aria-label="Discuss your software project with HexxaTech on WhatsApp"
            >
              Discuss Your Project

              <span aria-hidden="true">
                →
              </span>
            </a>

          </div>

        </section>

        {/* =====================================================
            FOOTER CREDIT
        ===================================================== */}

        <p className="cs-footer-credit">
          Developed by HexxaTech Softwares
        </p>

      </div>
    </main>
  );
}